"use client";

import { useMotionValue, type MotionValue } from "framer-motion";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import { useMotionPreference } from "@/hooks/use-motion-preference";
import "locomotive-scroll/locomotive-scroll.css";

/** Mirrors Locomotive Scroll v5's scrollCallback payload. */
export interface ScrollValues {
  scroll: number;
  limit: number;
  velocity: number;
  direction: number;
  /** Normalized 0 → 1 progress through the whole page. */
  progress: number;
}

type ScrollSubscriber = (values: ScrollValues) => void;
type ScrollTarget = number | string | HTMLElement;

interface ScrollApi {
  /** Live page progress (0 → 1). Read in rAF/useFrame loops; never triggers renders. */
  progress: { readonly current: number };
  /** Subscribe to every scroll tick. Returns an unsubscribe fn. */
  subscribe: (fn: ScrollSubscriber) => () => void;
  /** Scroll to a px offset, selector, or element. `immediate` jumps with no easing. */
  scrollTo: (target: ScrollTarget, options?: { offset?: number; immediate?: boolean }) => void;
}

const NOOP_API: ScrollApi = {
  progress: { current: 0 },
  subscribe: () => () => {},
  scrollTo: () => {},
};

const ScrollContext = createContext<ScrollApi>(NOOP_API);

/** Access the single app-wide scroll source (Locomotive Scroll v5, Lenis under the hood). */
export const useAppScroll = () => useContext(ScrollContext);

/**
 * The one and only scroll engine for the site: Locomotive Scroll v5 (smooth
 * scroll + parallax + progress), built on Lenis. It drives the real window
 * scroll, publishes progress as the `--scroll-progress` CSS variable, and fans
 * out every tick to subscribers. Nothing else reads scroll position directly —
 * which is exactly why there's no more Framer `useScroll` container detection.
 *
 * Reduced-motion users get native scrolling (no inertia) but the same progress
 * plumbing, so scroll-aware visuals still resolve correctly.
 */
export function LocomotiveScrollProvider({ children }: { children: ReactNode }) {
  const { reducedMotion } = useMotionPreference();
  const progress = useRef(0);
  const subscribers = useRef(new Set<ScrollSubscriber>());
  const locoRef = useRef<import("locomotive-scroll").default | null>(null);

  useEffect(() => {
    const root = document.documentElement;

    const emit = (values: ScrollValues) => {
      progress.current = Number.isFinite(values.progress) ? values.progress : 0;
      root.style.setProperty("--scroll-progress", progress.current.toFixed(4));
      root.style.setProperty("--scroll-velocity", (values.velocity || 0).toFixed(3));
      subscribers.current.forEach((fn) => fn(values));
    };

    // Native scroll that still publishes progress. Used for reduced-motion,
    // touch devices, and as the fallback if Locomotive ever fails to load.
    const attachNativeScroll = () => {
      let frame = 0;
      const measure = () => {
        frame = 0;
        const limit = root.scrollHeight - window.innerHeight;
        const scroll = window.scrollY;
        emit({
          scroll,
          limit,
          velocity: 0,
          direction: 1,
          progress: limit > 0 ? scroll / limit : 0,
        });
      };
      // Coalesce rapid scroll events into one update per frame so subscribers
      // (which read getBoundingClientRect) can't thrash layout on mobile.
      const onScroll = () => {
        if (!frame) frame = requestAnimationFrame(measure);
      };
      measure();
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        if (frame) cancelAnimationFrame(frame);
        window.removeEventListener("scroll", onScroll);
      };
    };

    // Smooth-scroll hijacking (Locomotive/Lenis) fights iOS momentum scrolling:
    // on touch it can leave the page unscrollable, and because the reveal
    // animations start hidden until scrolled into view, everything below the
    // fold then stays invisible. Touch devices get native scroll instead —
    // smooth scroll is a desktop-wheel enhancement only.
    const isTouch =
      typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

    if (reducedMotion || isTouch) {
      return attachNativeScroll();
    }

    // Dynamic import keeps Locomotive (and its window references) out of SSR.
    let cancelled = false;
    let loco: import("locomotive-scroll").default | null = null;
    let detachNative: (() => void) | null = null;
    import("locomotive-scroll")
      .then(({ default: LocomotiveScroll }) => {
        if (cancelled) return;
        loco = new LocomotiveScroll({
          lenisOptions: {
            lerp: 0.1,
            duration: 1.2,
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 1.5,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          },
          scrollCallback: emit,
        });
        locoRef.current = loco;
      })
      .catch(() => {
        // Never leave the page with dead scroll plumbing — fall back to native.
        if (!cancelled) detachNative = attachNativeScroll();
      });

    return () => {
      cancelled = true;
      loco?.destroy();
      locoRef.current = null;
      detachNative?.();
    };
  }, [reducedMotion]);

  const api = useMemo<ScrollApi>(
    () => ({
      progress,
      subscribe: (fn) => {
        subscribers.current.add(fn);
        return () => {
          subscribers.current.delete(fn);
        };
      },
      scrollTo: (target, options) => {
        const loco = locoRef.current;
        if (loco) {
          // Locomotive forwards extra opts (immediate/duration) to Lenis.
          loco.scrollTo(target, options as unknown as Parameters<typeof loco.scrollTo>[1]);
          return;
        }
        const behavior: ScrollBehavior = options?.immediate ? "auto" : "smooth";
        if (typeof target === "number") {
          window.scrollTo({ top: target, behavior });
          return;
        }
        const el = typeof target === "string" ? document.querySelector(target) : target;
        (el as HTMLElement | null)?.scrollIntoView({ behavior });
      },
    }),
    [],
  );

  return <ScrollContext.Provider value={api}>{children}</ScrollContext.Provider>;
}

/**
 * Local scroll progress (0 → 1) for one element, as a Framer MotionValue you can
 * feed into `useTransform`. Driven by the global Locomotive tick — no per-element
 * `useScroll({ target })`, so it never warns about scroll containers and never
 * needs the target ref hydrated before first paint.
 *
 * - `"exit"`  — 0 when the element's top hits the top of the viewport, 1 after
 *   it has scrolled up by its own height (good for hero fade-outs).
 * - `"pin"`   — 0 at entry, 1 when its bottom reaches the viewport bottom (good
 *   for tall pinned/sticky sections that scrub horizontally).
 */
export function useElementScrollProgress(
  ref: RefObject<HTMLElement | null>,
  mode: "exit" | "pin" = "pin",
): MotionValue<number> {
  const { subscribe } = useAppScroll();
  const value = useMotionValue(0);

  useEffect(() => {
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const span = mode === "exit" ? rect.height : Math.max(1, rect.height - vh);
      value.set(Math.min(1, Math.max(0, -rect.top / span)));
    };
    compute();
    const unsubscribe = subscribe(compute);
    window.addEventListener("resize", compute);
    return () => {
      unsubscribe();
      window.removeEventListener("resize", compute);
    };
  }, [ref, mode, subscribe, value]);

  return value;
}
